import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportingLeDataSessionPinPath } from '~/consts/reportingLeDataApi';

import { json_headers, request } from '~/services/common';

import type { LeDataSessionSummary } from '~/types/leData';

import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

import { reportingLeDataSessionsQueryKeys } from './useRecentReportingLeDataSessions';

type PinSessionInput = {
  sessionId: string;
  pinned: boolean;
};

type PinSessionContext = {
  previous?: Array<LeDataSessionSummary>;
};

/**
 * POST `/api/reporting/le-data/sessions/:sessionId/pin` with `{ pinned }`.
 *
 * A thread someone keeps returning to falls down a list ordered by recency
 * precisely because it is not the newest. The list is refetched on success
 * rather than patched, since pinning changes the order rather than one row.
 */
export function usePinReportingLeDataSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, pinned }: PinSessionInput) => {
      const url = resolveReportingLeDataUrl(reportingLeDataSessionPinPath(sessionId));
      await request(url, {
        method: 'POST',
        headers: json_headers,
        body: JSON.stringify({ pinned }),
        ignoreResponse: true,
      });
    },
    onMutate: async ({ sessionId, pinned }): Promise<PinSessionContext> => {
      await queryClient.cancelQueries({ queryKey: reportingLeDataSessionsQueryKeys.all });
      const previous = queryClient.getQueryData<Array<LeDataSessionSummary>>(reportingLeDataSessionsQueryKeys.all);
      const pinnedAt = pinned ? new Date().toISOString() : undefined;

      queryClient.setQueryData<Array<LeDataSessionSummary>>(reportingLeDataSessionsQueryKeys.all, (sessions) =>
        sessions?.map((session) =>
          session.sessionId === sessionId
            ? { ...session, ...(pinnedAt ? { pinnedAt } : { pinnedAt: undefined }) }
            : session,
        ),
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(reportingLeDataSessionsQueryKeys.all, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: reportingLeDataSessionsQueryKeys.all });
    },
  });
}
